import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Tooltip, Typography, alpha } from '@mui/material';

import { getScoreColor } from '../theme';

/**
 * AIConfidenceBadge — pure renderer for the `ai_confidence` payload returned
 * by the backend pattern-reliability service.
 *
 * The component is intentionally renderer-only: it never thresholds raw
 * probabilities or derives investor narrative.  The backend supplies:
 *   - `level`  — score-color level key (excellent | veryGood | good | fair | poor)
 *   - `label`  — bilingual `{vi, en, code}` label text (rendered in the badge)
 *   - `score`  — 0-100 integer (shown in the tooltip for precision)
 *   - `p_win_10d` / `p_win_20d` — raw probabilities for tooltip context
 *
 * The badge body shows the human-readable bilingual label so users see
 * "Tốt" / "Good" instead of a raw number.  The precise score and per-horizon
 * win probabilities live in the tooltip for users who want detail.
 *
 * If `confidence` is null/undefined (model unavailable or short history),
 * the component renders a muted dash placeholder so columns stay aligned.
 */
const AIConfidenceBadge = ({
  confidence,
  language,
  tooltipTitle,
  compact,
}) => {
  if (!confidence) {
    return (
      <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
        —
      </Typography>
    );
  }

  const level = confidence.level || 'unknown';
  const color = getScoreColor(level);
  const isVi = (language || '').toLowerCase().startsWith('vi');
  const label = confidence.label || {};
  const labelText = (isVi ? label.vi : label.en) || label.code || level;
  const score = Number.isFinite(confidence.score) ? confidence.score : null;
  const scoreCaption = isVi ? 'Điểm' : 'Score';

  const badge = (
    <Stack
      direction="row"
      spacing={0}
      alignItems="center"
      sx={{
        display: 'inline-flex',
        bgcolor: alpha(color, 0.12),
        color,
        border: `1px solid ${alpha(color, 0.35)}`,
        borderRadius: 0.85,
        px: compact ? 0.75 : 1,
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.2,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        {labelText}
      </Box>
    </Stack>
  );

  const tooltipBody = (
    <Box sx={{ fontSize: 11, minWidth: 140 }}>
      {tooltipTitle ? (
        <Typography sx={{ fontWeight: 700, fontSize: 11, mb: 0.4 }}>
          {tooltipTitle}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <span>{scoreCaption}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
          {score == null ? '—' : `${score} · ${labelText}`}
        </span>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <span>p(10d)</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {confidence.p_win_10d == null ? '—' : `${Math.round(confidence.p_win_10d * 100)}%`}
        </span>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <span>p(20d)</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {confidence.p_win_20d == null ? '—' : `${Math.round(confidence.p_win_20d * 100)}%`}
        </span>
      </Box>
      {confidence.model_version ? (
        <Box sx={{ opacity: 0.7, mt: 0.3 }}>v{confidence.model_version}</Box>
      ) : null}
    </Box>
  );

  return (
    <Tooltip arrow placement="top" title={tooltipBody}>
      <Box component="span" sx={{ display: 'inline-flex' }}>{badge}</Box>
    </Tooltip>
  );
};

AIConfidenceBadge.propTypes = {
  /**
   * Backend-supplied `ai_confidence` payload.  `null`/`undefined` is
   * rendered as a muted dash (used when the reliability model has no
   * prediction for that signal — e.g. short price history).
   */
  confidence: PropTypes.shape({
    score: PropTypes.number,
    level: PropTypes.oneOf([
      'excellent', 'veryGood', 'good', 'fair', 'poor', 'unknown',
    ]),
    label: PropTypes.shape({
      code: PropTypes.string,
      vi: PropTypes.string,
      en: PropTypes.string,
    }),
    p_win_10d: PropTypes.number,
    p_win_20d: PropTypes.number,
    model_version: PropTypes.string,
  }),
  /**
   * Active locale (e.g. 'vi' or 'en').  Selects which bilingual label to show.
   */
  language: PropTypes.string,
  /**
   * Optional heading shown above the probability rows in the tooltip.
   */
  tooltipTitle: PropTypes.string,
  /**
   * When true, uses a tighter horizontal padding for dense table cells.
   * Does not change which fields are shown — the badge always renders the
   * bilingual label and exposes the precise score in the tooltip.
   */
  compact: PropTypes.bool,
};

AIConfidenceBadge.defaultProps = {
  confidence: null,
  language: 'en',
  tooltipTitle: '',
  compact: false,
};

export default AIConfidenceBadge;
